// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: whiteboard documents. A board is a pad's second side: a list of Excalidraw
// elements plus the images they show. Personal boards follow their owner's devices
// through KONVO_BOARD_UPDATE; a chat's board is shared with its members through
// KONVO_CHANNEL_BOARD_UPDATE, with live cursors on KONVO_CHANNEL_BOARD_CURSOR.
//
// Saves send the whole scene and the version it came from. The server folds in anything
// newer it already has (per element, by version; see BoardMerge), so nobody's change is
// lost and there is nothing to resolve by hand. Image bytes are uploaded once, next to
// the scene, and fetched back on demand.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {builtinBoardFile} from '@app/features/konvo_board/state/BoardBuiltinFiles';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {
	type BoardElementLike,
	type BoardFileMeta,
	mergeBoardElements,
	referencedBoardFileIds,
} from '@konvo/constants/src/BoardMerge';
import {makeAutoObservable, observable, runInAction} from 'mobx';

const logger = new Logger('KonvoBoards');
const SAVE_DEBOUNCE_MS = 700;
const RETRY_MS = 5000;
const CURSOR_INTERVAL_MS = 120;
const CURSOR_TTL_MS = 10_000;

export type BoardScope = {kind: 'personal'; boardId: string} | {kind: 'channel'; channelId: string};
export type BoardStatus = 'idle' | 'loading' | 'saving' | 'saved' | 'offline';

export interface BoardPayload {
	board_id?: string;
	channel_id?: string;
	elements: Array<BoardElementLike>;
	files: Record<string, BoardFileMeta>;
	version: number;
	updated_at: string | null;
	updated_by: string | null;
	client_id?: string | null;
	merged?: boolean;
}

export interface BoardCursorPayload {
	channel_id: string;
	user_id: string;
	x: number | null;
	y: number | null;
	label?: string | null;
	until?: number;
	client_id?: string | null;
}

export interface BoardCursor {
	userId: string;
	x: number;
	y: number;
	label: string | null;
	until: number;
}

export interface BoardFileData {
	id: string;
	mimeType: string;
	dataURL: string;
	created: number;
}

function randomClientId(): string {
	try {
		return crypto.randomUUID();
	} catch {
		return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
	}
}

export function boardKey(scope: BoardScope): string {
	return scope.kind === 'personal' ? `personal:${scope.boardId}` : `channel:${scope.channelId}`;
}

/** Excalidraw's scene version: the sum of element versions, cheap to compare on every change. */
export function sceneVersion(elements: ReadonlyArray<{version: number}>): number {
	let sum = 0;
	for (const el of elements) sum += el.version;
	return sum;
}

function isPayload(body: unknown): body is BoardPayload {
	return Boolean(body) && Array.isArray((body as BoardPayload).elements);
}

export class BoardDoc {
	readonly scope: BoardScope;
	readonly key: string;
	readonly clientId = randomClientId();
	elements: ReadonlyArray<BoardElementLike> = [];
	files: Record<string, BoardFileMeta> = {};
	version = 0;
	updatedAt: string | null = null;
	updatedBy: string | null = null;
	status: BoardStatus = 'idle';
	loaded = false;
	dirty = false;
	/** Bumped whenever someone else's changes land, so an open canvas pulls them in. */
	remoteTick = 0;
	cursors = observable.map<string, BoardCursor>();
	private saveTimer: ReturnType<typeof setTimeout> | null = null;
	private retryTimer: ReturnType<typeof setTimeout> | null = null;
	private inFlight: Promise<void> | null = null;
	private fileData = new Map<string, BoardFileData>();
	private fileLoads = new Map<string, Promise<BoardFileData | null>>();
	private uploads = new Map<string, Promise<boolean>>();
	private cursorTimer: ReturnType<typeof setTimeout> | null = null;
	private cursorSentAt = 0;
	private pendingCursor: {x: number | null; y: number | null; label: string | null} | null = null;

	constructor(scope: BoardScope) {
		this.scope = scope;
		this.key = boardKey(scope);
		makeAutoObservable<
			this,
			| 'saveTimer'
			| 'retryTimer'
			| 'inFlight'
			| 'fileData'
			| 'fileLoads'
			| 'uploads'
			| 'cursorTimer'
			| 'cursorSentAt'
			| 'pendingCursor'
		>(
			this,
			{
				scope: false,
				key: false,
				clientId: false,
				elements: observable.ref,
				files: observable.ref,
				saveTimer: false,
				retryTimer: false,
				inFlight: false,
				fileData: false,
				fileLoads: false,
				uploads: false,
				cursorTimer: false,
				cursorSentAt: false,
				pendingCursor: false,
			},
			{autoBind: true},
		);
	}

	private get endpoint(): string {
		return this.scope.kind === 'personal'
			? Endpoints.KONVO_BOARD(this.scope.boardId)
			: Endpoints.KONVO_CHANNEL_BOARD(this.scope.channelId);
	}

	private filesEndpoint(): string {
		return this.scope.kind === 'personal'
			? Endpoints.KONVO_BOARD_FILES(this.scope.boardId)
			: Endpoints.KONVO_CHANNEL_BOARD_FILES(this.scope.channelId);
	}

	private fileEndpoint(fileId: string): string {
		return this.scope.kind === 'personal'
			? Endpoints.KONVO_BOARD_FILE(this.scope.boardId, fileId)
			: Endpoints.KONVO_CHANNEL_BOARD_FILE(this.scope.channelId, fileId);
	}

	get liveElements(): Array<BoardElementLike> {
		return this.elements.filter((el) => !el.isDeleted);
	}

	get isEmpty(): boolean {
		return this.liveElements.length === 0;
	}

	/** Other people on this board right now (shared boards only). */
	get liveCursors(): Array<BoardCursor> {
		const now = Date.now();
		return [...this.cursors.values()].filter((cursor) => cursor.until > now);
	}

	async load(): Promise<void> {
		if (this.dirty) return;
		runInAction(() => {
			if (!this.loaded) this.status = 'loading';
		});
		try {
			const response = await http.get<BoardPayload>(this.endpoint, {mode: 'silent'});
			if (!response.ok) throw new Error(`load failed with ${response.status}`);
			if (!isPayload(response.body)) throw new Error('load returned no board');
			const body = response.body;
			runInAction(() => {
				if (this.dirty) return;
				this.accept(body, true);
				this.loaded = true;
				this.status = 'saved';
			});
		} catch (error) {
			logger.warn('Failed to load board', error);
			runInAction(() => {
				if (!this.loaded) this.status = 'offline';
			});
		}
	}

	/** Takes the server's scene: merged into what we have so unsaved local work survives. */
	private accept(body: BoardPayload, remote: boolean): void {
		const merged = mergeBoardElements(this.elements, body.elements ?? []);
		if (merged.changed || this.elements.length !== merged.elements.length) {
			this.elements = merged.elements;
			if (remote) this.remoteTick += 1;
		}
		const files = {...this.files, ...(body.files ?? {})};
		if (Object.keys(files).length !== Object.keys(this.files).length) this.files = files;
		this.version = Math.max(this.version, body.version ?? 0);
		this.updatedAt = body.updated_at ?? this.updatedAt;
		this.updatedBy = body.updated_by ?? this.updatedBy;
	}

	/** The canvas changed. Saved after a short pause. */
	setLocal(elements: ReadonlyArray<BoardElementLike>): void {
		if (sceneVersion(elements) === sceneVersion(this.elements) && elements.length === this.elements.length) return;
		this.elements = elements;
		this.dirty = true;
		this.loaded = true;
		this.status = 'saving';
		this.scheduleSave();
	}

	/** Gateway: someone saved this board (maybe us, echoed back). */
	applyRemote(payload: BoardPayload): void {
		if (payload.client_id && payload.client_id === this.clientId) {
			this.version = Math.max(this.version, payload.version ?? 0);
			return;
		}
		if (!isPayload(payload)) return;
		this.accept(payload, true);
		if (!this.dirty) this.status = 'saved';
	}

	flush(): void {
		if (this.saveTimer != null) {
			clearTimeout(this.saveTimer);
			this.saveTimer = null;
		}
		if (this.dirty) void this.save();
	}

	private scheduleSave(): void {
		if (this.saveTimer != null) clearTimeout(this.saveTimer);
		this.saveTimer = setTimeout(() => {
			this.saveTimer = null;
			void this.save();
		}, SAVE_DEBOUNCE_MS);
	}

	private async save(): Promise<void> {
		if (this.inFlight) {
			this.scheduleSave();
			return;
		}
		const snapshot = this.elements;
		const run = (async () => {
			try {
				// Images must reach the server before the scene that shows them.
				await this.uploadMissingFiles(snapshot);
				const response = await http.put<BoardPayload>(this.endpoint, {
					body: {elements: snapshot, files: this.files, base_version: this.version, client_id: this.clientId},
					mode: 'silent',
				});
				if (!response.ok) throw new Error(`save failed with ${response.status}`);
				runInAction(() => {
					const body = response.body;
					if (isPayload(body)) this.accept(body, Boolean(body.merged));
					if (this.elements === snapshot) {
						this.dirty = false;
						this.status = 'saved';
					}
				});
				if (this.elements !== snapshot) this.scheduleSave();
			} catch (error) {
				logger.warn('Failed to save board', error);
				runInAction(() => {
					this.status = 'offline';
				});
				if (this.retryTimer != null) clearTimeout(this.retryTimer);
				this.retryTimer = setTimeout(() => {
					this.retryTimer = null;
					if (this.dirty) void this.save();
				}, RETRY_MS);
			}
		})();
		this.inFlight = run;
		try {
			await run;
		} finally {
			if (this.inFlight === run) this.inFlight = null;
		}
	}

	// ---- images ----

	/** An image the canvas added: kept here until the scene that uses it is saved. */
	rememberFile(file: BoardFileData): void {
		if (builtinBoardFile(file.id)) return;
		this.fileData.set(file.id, file);
	}

	hasFileData(fileId: string): boolean {
		return this.fileData.has(fileId) || builtinBoardFile(fileId) !== null;
	}

	private async uploadMissingFiles(elements: ReadonlyArray<BoardElementLike>): Promise<void> {
		const wanted = [...referencedBoardFileIds(elements)].filter((id) => !this.files[id] && this.fileData.has(id));
		const results = await Promise.all(wanted.map((id) => this.uploadFile(id)));
		if (results.some((ok) => !ok)) throw new Error('image upload failed');
	}

	private uploadFile(fileId: string): Promise<boolean> {
		const existing = this.uploads.get(fileId);
		if (existing) return existing;
		const file = this.fileData.get(fileId);
		if (!file) return Promise.resolve(false);
		const run = (async () => {
			try {
				const response = await http.post<BoardFileMeta>(this.filesEndpoint(), {
					body: {id: file.id, mime_type: file.mimeType, data_url: file.dataURL},
					mode: 'silent',
				});
				if (!response.ok || !response.body) throw new Error(`upload failed with ${response.status}`);
				const meta = response.body;
				runInAction(() => {
					this.files = {...this.files, [fileId]: meta};
				});
				return true;
			} catch (error) {
				logger.warn('Failed to upload board image', error);
				this.uploads.delete(fileId);
				return false;
			}
		})();
		this.uploads.set(fileId, run);
		return run;
	}

	/** Image data for the given ids, from memory or the server. Missing ones are left out. */
	async loadFiles(fileIds: Iterable<string>): Promise<Array<BoardFileData>> {
		const results = await Promise.all([...fileIds].map((id) => this.loadFile(id)));
		return results.filter((file): file is BoardFileData => file !== null);
	}

	private loadFile(fileId: string): Promise<BoardFileData | null> {
		const builtin = builtinBoardFile(fileId);
		if (builtin) return Promise.resolve(builtin);
		const cached = this.fileData.get(fileId);
		if (cached) return Promise.resolve(cached);
		const pending = this.fileLoads.get(fileId);
		if (pending) return pending;
		const run = (async () => {
			try {
				const response = await http.get<{id: string; mime_type: string; data_url: string}>(this.fileEndpoint(fileId), {
					mode: 'silent',
				});
				if (!response.ok || !response.body?.data_url) return null;
				const file: BoardFileData = {
					id: fileId,
					mimeType: response.body.mime_type,
					dataURL: response.body.data_url,
					created: this.files[fileId]?.created ?? Date.now(),
				};
				this.fileData.set(fileId, file);
				return file;
			} catch (error) {
				logger.warn('Failed to load board image', error);
				return null;
			} finally {
				this.fileLoads.delete(fileId);
			}
		})();
		this.fileLoads.set(fileId, run);
		return run;
	}

	// ---- live cursors (shared boards) ----

	/** Our pointer moved on the board, in scene coordinates. Sent a few times a second at most. */
	sendCursor(x: number, y: number, label: string | null = null): void {
		if (this.scope.kind !== 'channel') return;
		this.pendingCursor = {x, y, label};
		this.pumpCursor();
	}

	/** We left the board (closed it, switched to notes). */
	leaveCursor(): void {
		if (this.scope.kind !== 'channel') return;
		if (this.cursorTimer != null) {
			clearTimeout(this.cursorTimer);
			this.cursorTimer = null;
		}
		this.pendingCursor = null;
		void this.postCursor({x: null, y: null, label: null});
	}

	private pumpCursor(): void {
		if (this.cursorTimer != null) return;
		const wait = Math.max(0, this.cursorSentAt + CURSOR_INTERVAL_MS - Date.now());
		this.cursorTimer = setTimeout(() => {
			this.cursorTimer = null;
			const next = this.pendingCursor;
			this.pendingCursor = null;
			if (!next) return;
			this.cursorSentAt = Date.now();
			void this.postCursor(next);
		}, wait);
	}

	private async postCursor(cursor: {x: number | null; y: number | null; label: string | null}): Promise<void> {
		if (this.scope.kind !== 'channel') return;
		try {
			await http.post(Endpoints.KONVO_CHANNEL_BOARD_CURSOR(this.scope.channelId), {
				body: {...cursor, client_id: this.clientId},
				mode: 'silent',
			});
		} catch {
			// Cursors are best effort; the next move sends a fresh one.
		}
	}

	/** Gateway: someone's pointer on this board. */
	applyCursor(payload: BoardCursorPayload, myUserId: string | null): void {
		if (payload.client_id === this.clientId || (myUserId && payload.user_id === myUserId)) return;
		if (payload.x == null || payload.y == null) {
			this.cursors.delete(payload.user_id);
			return;
		}
		this.cursors.set(payload.user_id, {
			userId: payload.user_id,
			x: payload.x,
			y: payload.y,
			label: payload.label ?? null,
			until:
				typeof payload.until === 'number'
					? Math.min(payload.until, Date.now() + CURSOR_TTL_MS)
					: Date.now() + CURSOR_TTL_MS,
		});
	}
}

class KonvoBoardsStore {
	private docs = new Map<string, BoardDoc>();

	get(scope: BoardScope): BoardDoc {
		const key = boardKey(scope);
		let doc = this.docs.get(key);
		if (!doc) {
			doc = new BoardDoc(scope);
			this.docs.set(key, doc);
		}
		return doc;
	}

	/** Only boards somebody has opened are tracked; the rest load fresh when opened. */
	peek(scope: BoardScope): BoardDoc | undefined {
		return this.docs.get(boardKey(scope));
	}

	flushAll(): void {
		for (const doc of this.docs.values()) doc.flush();
	}
}

export default new KonvoBoardsStore();
